import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  dedupeWorkflowPerformanceLookupByName,
  listWorkflowPerformanceAllocationOfficersCascade,
  listWorkflowPerformanceTemplates,
  listWorkflowPerformanceWorkflowStagesCascade,
  mapWorkflowPerformanceLookupNamesToIds,
  mapWorkflowTemplateStatusNamesToCodes,
  sortWorkflowPerformanceLookupOptions,
  workflowPerformanceLookupDisplayNames,
  WORKFLOW_PERFORMANCE_TEMPLATE_STATUSES,
  type WorkflowPerformanceLookupOption,
  type WorkflowPerformanceTemplateStatusOption,
} from '../../services/workflowPerformanceReports';
import { getFileCategories, getSubFileCategories } from '../../services/criminalCaseRequests';
import { REPORT_WORKFLOW_TEMPLATES } from '../../data/reports';

export function useWorkflowPerformanceLookups(
  selectedWorkflowTemplateNames: string[],
  selectedFileCategoryNames: string[],
  selectedSubFileCategoryNames: string[],
) {
  const { user } = useAuth();
  const [templateOptionsAll, setTemplateOptionsAll] = useState<WorkflowPerformanceLookupOption[]>([]);
  const [workflowStageOptionsAll, setWorkflowStageOptionsAll] = useState<WorkflowPerformanceLookupOption[]>([]);
  const [allocationOfficerOptionsAll, setAllocationOfficerOptionsAll] = useState<WorkflowPerformanceLookupOption[]>([]);
  const [fileCategoryOptions, setFileCategoryOptions] = useState<WorkflowPerformanceLookupOption[]>([]);
  const [subFileCategoryOptions, setSubFileCategoryOptions] = useState<WorkflowPerformanceLookupOption[]>([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [resolvedSubCategoryKey, setResolvedSubCategoryKey] = useState<string | null>(null);
  const [resolvedWorkflowStageKey, setResolvedWorkflowStageKey] = useState<string | null>(null);
  const [resolvedAllocationOfficerKey, setResolvedAllocationOfficerKey] = useState<string | null>(null);

  const selectedWorkflowTemplateIds = useMemo(
    () => mapWorkflowPerformanceLookupNamesToIds(templateOptionsAll, selectedWorkflowTemplateNames, true),
    [templateOptionsAll, selectedWorkflowTemplateNames],
  );

  const workflowStageRequestKey = useMemo(() => {
    if (!selectedWorkflowTemplateNames.length) return '';
    if (selectedWorkflowTemplateIds.length === 0) return '|pending|';
    return JSON.stringify({ workflowTemplateIds: [...selectedWorkflowTemplateIds].sort() });
  }, [selectedWorkflowTemplateNames, selectedWorkflowTemplateIds]);

  const subCategoryRequestKey = useMemo(() => {
    if (!selectedFileCategoryNames.length) return '';
    const categoryIds = selectedFileCategoryNames
      .map((name) => fileCategoryOptions.find((c) => c.name === name)?.id)
      .filter((id): id is string => Boolean(id));
    return categoryIds.length ? [...categoryIds].sort().join('|') : '';
  }, [selectedFileCategoryNames, fileCategoryOptions]);

  const allocationOfficerRequestKey = useMemo(() => {
    const workflowTemplateIds = selectedWorkflowTemplateIds;
    const categoryIds = selectedFileCategoryNames
      .map((name) => fileCategoryOptions.find((c) => c.name === name)?.id)
      .filter((id): id is string => Boolean(id));
    const subCategoryIds = selectedSubFileCategoryNames
      .map((name) => subFileCategoryOptions.find((s) => s.name === name)?.id)
      .filter((id): id is string => Boolean(id));

    const hasParentSelection =
      selectedWorkflowTemplateNames.length > 0 ||
      selectedFileCategoryNames.length > 0 ||
      selectedSubFileCategoryNames.length > 0;
    const hasResolvableIds =
      workflowTemplateIds.length > 0 || categoryIds.length > 0 || subCategoryIds.length > 0;
    if (hasParentSelection && !hasResolvableIds) return '|pending|';

    return JSON.stringify({
      workflowTemplateIds: [...workflowTemplateIds].sort(),
      categoryIds: [...categoryIds].sort(),
      subCategoryIds: [...subCategoryIds].sort(),
    });
  }, [
    selectedWorkflowTemplateNames,
    selectedWorkflowTemplateIds,
    selectedFileCategoryNames,
    selectedSubFileCategoryNames,
    fileCategoryOptions,
    subFileCategoryOptions,
  ]);

  const subLookupsLoading =
    subCategoryRequestKey !== '' && resolvedSubCategoryKey !== subCategoryRequestKey;
  const workflowStagesLoading =
    lookupsLoaded &&
    (workflowStageRequestKey === '|pending|' ||
      (workflowStageRequestKey !== '' && resolvedWorkflowStageKey !== workflowStageRequestKey));
  const allocationOfficersLoading =
    lookupsLoaded &&
    (allocationOfficerRequestKey === '|pending|' ||
      resolvedAllocationOfficerKey !== allocationOfficerRequestKey);
  const activeSubFileCategoryOptions =
    subCategoryRequestKey === '' || resolvedSubCategoryKey !== subCategoryRequestKey
      ? []
      : subFileCategoryOptions;
  const activeWorkflowStageOptions = useMemo(
    () =>
      workflowStageRequestKey === '' || resolvedWorkflowStageKey !== workflowStageRequestKey
        ? []
        : workflowStageOptionsAll,
    [workflowStageRequestKey, resolvedWorkflowStageKey, workflowStageOptionsAll],
  );

  const templateOptions = useMemo(
    () => dedupeWorkflowPerformanceLookupByName(templateOptionsAll),
    [templateOptionsAll],
  );
  const allocationOfficerOptions = useMemo(() => {
    if (allocationOfficerRequestKey === '|pending|') return [];
    if (resolvedAllocationOfficerKey !== allocationOfficerRequestKey) return [];
    return dedupeWorkflowPerformanceLookupByName(allocationOfficerOptionsAll);
  }, [
    allocationOfficerOptionsAll,
    allocationOfficerRequestKey,
    resolvedAllocationOfficerKey,
  ]);
  const workflowStageItems = useMemo(() => {
    const sorted = sortWorkflowPerformanceLookupOptions(activeWorkflowStageOptions);
    return sorted.map((o) => ({ value: o.id, label: o.name }));
  }, [activeWorkflowStageOptions]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;

    void Promise.all([listWorkflowPerformanceTemplates(), getFileCategories()]).then(([templates, cats]) => {
      if (!alive) return;
      if (templates.success) setTemplateOptionsAll(templates.data);
      if (cats.success) setFileCategoryOptions(cats.data.map((c) => ({ id: c.id, name: c.name })));
      setLookupsLoaded(true);
    });

    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!subCategoryRequestKey) return;
    let alive = true;
    const categoryIds = subCategoryRequestKey.split('|');
    void Promise.all(categoryIds.map((id) => getSubFileCategories(id))).then((results) => {
      if (!alive) return;
      const merged = new Map<string, WorkflowPerformanceLookupOption>();
      for (const res of results) {
        if (!res.success) continue;
        for (const item of res.data) merged.set(item.id, { id: item.id, name: item.name });
      }
      setSubFileCategoryOptions([...merged.values()].sort((a, b) => a.name.localeCompare(b.name)));
      setResolvedSubCategoryKey(subCategoryRequestKey);
    });
    return () => {
      alive = false;
    };
  }, [subCategoryRequestKey]);

  useEffect(() => {
    if (!lookupsLoaded) return;

    const requestKey = workflowStageRequestKey;
    if (requestKey === '' || requestKey === '|pending|') return;

    let alive = true;
    const controller = new AbortController();
    const parsed = JSON.parse(requestKey) as { workflowTemplateIds: string[] };

    void listWorkflowPerformanceWorkflowStagesCascade({
      workflowTemplateIds: parsed.workflowTemplateIds,
      signal: controller.signal,
    }).then((res) => {
      if (!alive || controller.signal.aborted) return;
      if (res.success) setWorkflowStageOptionsAll(res.data);
      else setWorkflowStageOptionsAll([]);
      setResolvedWorkflowStageKey(requestKey);
    });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [workflowStageRequestKey, lookupsLoaded]);

  useEffect(() => {
    if (!lookupsLoaded) return;

    const requestKey = allocationOfficerRequestKey;
    if (requestKey === '|pending|') return;

    let alive = true;
    const controller = new AbortController();
    const parsed = JSON.parse(requestKey) as {
      workflowTemplateIds: string[];
      categoryIds: string[];
      subCategoryIds: string[];
    };

    void listWorkflowPerformanceAllocationOfficersCascade({
      workflowTemplateIds: parsed.workflowTemplateIds,
      categoryIds: parsed.categoryIds,
      subCategoryIds: parsed.subCategoryIds,
      signal: controller.signal,
    }).then((res) => {
      if (!alive || controller.signal.aborted) return;
      if (res.success) setAllocationOfficerOptionsAll(res.data);
      else setAllocationOfficerOptionsAll([]);
      setResolvedAllocationOfficerKey(requestKey);
    });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [allocationOfficerRequestKey, lookupsLoaded]);

  const statusOptions: WorkflowPerformanceTemplateStatusOption[] = WORKFLOW_PERFORMANCE_TEMPLATE_STATUSES;

  return {
    workflowTemplateNames: workflowPerformanceLookupDisplayNames(templateOptions, REPORT_WORKFLOW_TEMPLATES),
    workflowStageItems,
    allocationOfficerNames: allocationOfficerOptions.map((o) => o.name),
    fileCategoryNames: fileCategoryOptions.map((c) => c.name),
    subFileCategoryNames: activeSubFileCategoryOptions.map((s) => s.name),
    workflowTemplateStatusNames: statusOptions.map((s) => s.name),
    mapWorkflowTemplateNamesToIds: (names: string[]) =>
      mapWorkflowPerformanceLookupNamesToIds(templateOptionsAll, names, true),
    mapWorkflowStageIds: (ids: string[]) => ids.filter(Boolean),
    mapAllocationOfficerNamesToIds: (names: string[]) =>
      mapWorkflowPerformanceLookupNamesToIds(allocationOfficerOptions, names, true),
    mapWorkflowTemplateStatusNamesToCodes: (names: string[]) =>
      mapWorkflowTemplateStatusNamesToCodes(statusOptions, names),
    mapFileCategoryNamesToIds: (names: string[]) =>
      mapWorkflowPerformanceLookupNamesToIds(fileCategoryOptions, names),
    mapSubFileCategoryNamesToIds: (names: string[]) =>
      mapWorkflowPerformanceLookupNamesToIds(activeSubFileCategoryOptions, names),
    subFileCategoryDisabled: selectedFileCategoryNames.length === 0,
    workflowStageDisabled: selectedWorkflowTemplateNames.length === 0,
    lookupsLoading: !lookupsLoaded,
    subLookupsLoading,
    workflowStagesLoading,
    allocationOfficersLoading,
  };
}
